interface DownloadResult {
  status: 'success' | 'failed'
  filename?: string
  link: string
  error?: string
}

interface DownloadSummary {
  status: 'complete' | 'partial' | 'error'
  total: number
  successCount: number
  failedCount: number
  results: Array<DownloadResult | { status: 'failed'; link: string; error: string }>
}

/**
 * Downloads multiple files from an array of URLs with proper error handling and cleanup
 * @param links Array of file URLs to download
 * @returns Promise with download summary
 */
export const downloadAllFilesUsingLinks = async (links: string[]): Promise<DownloadSummary> => {
  if (!links.length) {
    return {
      status: 'complete',
      total: 0,
      successCount: 0,
      failedCount: 0,
      results: []
    }
  }

  try {
    const downloadResults = await Promise.allSettled(
      links.map(async (link, index) => {
        try {
          // Extract and clean filename
          const filename = getCleanFilename(link)

          // Verify the link is accessible
          const response = await fetch(link)
          if (!response.ok) {
            throw new Error(`HTTP ${response.status} - ${response.statusText}`)
          }

          // Get content type as fallback for missing extensions
          const contentType = response.headers.get('content-type')
          const finalFilename = ensureFileExtension(filename, contentType)

          // Create and trigger download
          const blob = await response.blob()
          triggerFileDownload(blob, finalFilename)

          return {
            status: 'success' as const,
            filename: finalFilename,
            link
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error'
          console.error(`Failed to download ${link}:`, errorMessage)
          return {
            status: 'failed' as const,
            link,
            error: errorMessage
          }
        }
      })
    )

    // Process results
    const results = downloadResults.map(result =>
      result.status === 'fulfilled' ? result.value : {
        status: 'failed' as const,
        link: result.reason.link || 'unknown',
        error: result.reason.error || 'Unknown error'
      }
    )

    const successCount = results.filter(r => r.status === 'success').length
    const failedCount = results.filter(r => r.status === 'failed').length

    return {
      status: failedCount === 0 ? 'complete' : 'partial',
      total: links.length,
      successCount,
      failedCount,
      results
    }
  } catch (error) {
    console.error('Critical error in downloadAllFilesUsingLinks:', error)
    return {
      status: 'error',
      total: links.length,
      successCount: 0,
      failedCount: links.length,
      results: links.map(link => ({
        status: 'failed',
        link,
        error: error instanceof Error ? error.message : 'Critical download error'
      }))
    }
  }
}

/**
 * Extracts and cleans filename from URL
 */
const getCleanFilename = (url: string): string => {
  try {
    // Handle both standard and encoded URLs
    const decodedUrl = decodeURIComponent(url)
    const baseUrl = decodedUrl.split('?')[0]?.split('#')[0] // Remove query and hash
    return baseUrl.split('/').pop() || `file-${Date.now()}`
  } catch {
    return `file-${Date.now()}`
  }
}

/**
 * Ensures filename has proper extension
 */
const ensureFileExtension = (filename: string, contentType?: string | null): string => {
  if (filename.includes('.')) return filename

  // Try to get extension from content type
  const extension = contentType?.split('/')[1]?.split(';')[0] || 'bin'
  return `${filename}.${extension}`
}

/**
 * Triggers file download in browser
 */
const triggerFileDownload = (blob: Blob, filename: string): void => {
  const downloadUrl = URL.createObjectURL(blob)
  const anchor = document.createElement('a')

  anchor.href = downloadUrl
  anchor.download = filename
  anchor.style.display = 'none'
  document.body.appendChild(anchor)
  anchor.click()

  // Cleanup
  setTimeout(() => {
    document.body.removeChild(anchor)
    URL.revokeObjectURL(downloadUrl)
  }, 100)
}
